"use client";

import Link from "next/link";
import { usePostHog } from "posthog-js/react";
import type { ReactNode } from "react";
import type { SalaryPageType } from "./salary-page-view-beacon";

type Props = {
	/** Always a same-zone `/gaji/[role]` path — /gaji is not a zoned path (src/lib/zones.ts), so a plain next/link is safe. */
	href: string;
	/**
	 * Destination role slug (RoleBenchmark.id), e.g. "software-engineer".
	 * Named `roleId`, not `role`, so a literal string value here doesn't trip
	 * Biome's `lint/a11y/useValidAriaRole` (it flags any JSX attribute named
	 * `role`, custom-component props included, when given a string literal).
	 * The emitted PostHog property is still `role` per the analytics contract.
	 */
	roleId: string;
	pageType: SalaryPageType;
	locale: string;
	className?: string;
	children: ReactNode;
};

/**
 * Wraps a link to another role's salary page (`/gaji/[role]`) across the
 * salary-content page family (guide index, per-role, comparison, ranking)
 * and fires `salary_role_clicked` on click, tagged with the destination
 * role and the originating `page_type` so per-role CTR is comparable
 * across surfaces.
 */
export function SalaryRoleLink({
	href,
	roleId,
	pageType,
	locale,
	className,
	children,
}: Props) {
	const posthog = usePostHog();
	return (
		<Link
			href={href}
			onClick={() =>
				posthog?.capture("salary_role_clicked", {
					role: roleId,
					page_type: pageType,
					locale,
				})
			}
			className={className}
		>
			{children}
		</Link>
	);
}
